import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { MerchandiseItem, MerchandiseOrder, InvoiceItem, Gelanggang, Role, UserProfile, GelanggangDetail } from '../../types';
import { saveInvoiceDB, getMembersDB, getGelanggangDB } from '../../services/db';
import { useMerchandiseData } from '../../hooks/useMerchandiseData';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  ShoppingBag,
  Tag,
  ClipboardList,
  Package,
  Plus,
  Check,
  X,
  CheckCircle,
  AlertTriangle,
  ShieldAlert,
  ExternalLink,
  Search,
  ShoppingCart,
  QrCode,
  MessageSquare,
  ArrowLeft,
  ChevronRight,
  Edit3,
  Info,
  Ruler,
  Lock,
  Shield,
  MapPin
} from 'lucide-react';
import { isSuperAdmin, isUserManagement, formatSellerName, isFamilyMember, isChildOfParent } from '../../utils/formatters';
import { DuitNowQrModal } from '../../components/merchandise/DuitNowQrModal';
import { getUniformPriceBySize, getProductPriceBySize, getProductPriceRange } from '../../utils/pricing';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';
import { ProductEditModal } from '../../components/merchandise/ProductEditModal';
import { ASMM_BENGKUNG_RANKS, toDbBengkungCode } from '../../data/bengkungRanks';

// Bengkung rank tiers: Hitam (0) -> Putih (1) -> Merah (2) -> Kuning (3) -> Biru (4) -> Guru Utama (5)
function getBengkungColorTier(text?: string): number {
  if (!text) return 0;
  const lower = text.toLowerCase();
  if (lower.includes('guru utama')) return 5;
  if (lower.includes('biru')) return 4;
  if (lower.includes('kuning')) return 3;
  if (lower.includes('merah')) return 2;
  if (lower.includes('putih')) return 1;
  if (lower.includes('hitam')) return 0;
  return 0;
}

const BENGKUNG_TIER_NAMES = [
  'Bengkung Hitam',
  'Bengkung Putih',
  'Bengkung Merah',
  'Bengkung Kuning',
  'Bengkung Biru',
  'Bengkung Guru Utama'
];

// Resolves rank index in official ASMM_BENGKUNG_RANKS
function getRankIndex(bengkungStr?: string): number {
  if (!bengkungStr) return 0;
  const dbCode = toDbBengkungCode(bengkungStr);
  const idx = ASMM_BENGKUNG_RANKS.findIndex(r => r.dbCode === dbCode);
  if (idx !== -1) return idx;
  const byCode = ASMM_BENGKUNG_RANKS.findIndex(r => r.code.toLowerCase() === bengkungStr.toLowerCase());
  return byCode !== -1 ? byCode : 0;
}

// Extracts numeric cula count from variation label (e.g. 'Cula 2' -> 2, 'Kosong' -> 0)
function getCulaNumber(variationStr?: string): number {
  if (!variationStr) return 0;
  if (variationStr.toLowerCase().includes('kosong')) return 0;
  const match = variationStr.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

// Maximum allowed cula count per color according to ASMM syllabus hierarchy
function getMaxCulaForColor(colorStr?: string): number {
  if (!colorStr) return 0;
  const lower = colorStr.toLowerCase();
  if (lower.includes('kuning')) return 7;
  if (lower.includes('merah')) return 3;
  if (lower.includes('putih')) return 3;
  if (lower.includes('hitam')) return 3;
  return 0;
}

// Validates whether a specific cula variation is unlocked for the member and family
function isBengkungVariationLocked(
  variationStr: string,
  colorStr: string,
  user: UserProfile | null,
  maxFamilyRankIndex: number
): { isLocked: boolean; reason?: string } {
  if (!user) {
    return { isLocked: true, reason: 'Sila log masuk untuk menempah bengkung mengikut kelayakan.' };
  }

  const culaCount = getCulaNumber(variationStr);
  const maxCula = getMaxCulaForColor(colorStr);

  // Validate if this cula number exists in the belt's curriculum
  if (culaCount > maxCula) {
    return {
      isLocked: true,
      reason: `Bengkung ${colorStr} hanya mempunyai sehingga ${maxCula === 0 ? 'tiada cula' : `${maxCula} Cula`} dalam silibus rasmi persatuan.`
    };
  }

  // Construct target rank name e.g. "Putih" or "Putih 2"
  const targetCode = culaCount === 0 ? colorStr : `${colorStr} ${culaCount}`;
  const targetDbCode = toDbBengkungCode(targetCode);
  const targetIndex = ASMM_BENGKUNG_RANKS.findIndex(r => r.dbCode === targetDbCode);
  const targetRank = targetIndex !== -1 ? ASMM_BENGKUNG_RANKS[targetIndex] : null;

  if (targetIndex === -1 || targetIndex > maxFamilyRankIndex) {
    return {
      isLocked: true,
      reason: `Peringkat ${targetCode} (${targetRank?.rankTitle || 'Cula Terkunci'}) belum dicapai oleh anda atau ahli keluarga berdaftar.`
    };
  }

  return { isLocked: false };
}

interface MerchandisePageProps {
  onNavigateTab?: (tab: string) => void;
}

export const MerchandisePage: React.FC<MerchandisePageProps> = ({ onNavigateTab }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { items, isLoading, saveOrder, saveItem } = useMerchandiseData();

  const isManagement = Boolean(currentUser && isUserManagement(currentUser));

  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [placedOrderForQr, setPlacedOrderForQr] = useState<MerchandiseOrder | null>(null);
  const [pendingInvoiceForQr, setPendingInvoiceForQr] = useState<InvoiceItem | null>(null);

  // Edit / Update Product Modal State
  const [editingProduct, setEditingProduct] = useState<MerchandiseItem | null>(null);
  const [isProductEditOpen, setIsProductEditOpen] = useState(false);

  const [members, setMembers] = useState<UserProfile[]>([]);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);

  useEffect(() => {
    getMembersDB().then(setMembers).catch(() => { });
    getGelanggangDB().then(setGelanggangList).catch(() => { });
  }, []);

  const getGelanggangAddress = (gName?: string): string => {
    if (!gName) return '';
    const cleanName = gName.trim().toLowerCase();
    const found = gelanggangList.find(g =>
      g.name.toLowerCase() === cleanName ||
      cleanName.includes(g.name.toLowerCase()) ||
      g.name.toLowerCase().includes(cleanName)
    );
    if (found?.address) return found.address;
    if (found?.location) return found.location;

    // Static fallback for official ASMM gelanggang
    if (cleanName.includes('pusaka') || cleanName.includes('warisan')) {
      return 'Padang Awam Taman Sejahtera, 09600 Lunas, Kedah';
    }
    if (cleanName.includes('wadi') || cleanName.includes('nour')) {
      return 'Pusat Riadah Wadi AnNour, Kampung Simpang Tiga Keladi, 09000 Kulim, Kedah';
    }
    if (cleanName.includes('paya') || cleanName.includes('besar')) {
      return 'SK Paya Besar, Lunas, 09000 Kulim, Kedah';
    }
    return 'Pusat Latihan Rasmi PSSASMM Kedah';
  };

  // Routing State for Single Product View
  const [selectedProductId, setSelectedProductId] = useState<string | null>(() => {
    const parts = window.location.pathname.split('/');
    if (parts.length > 2 && parts[1] === 'merchandise') {
      return parts[2];
    }
    return null;
  });

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const parts = window.location.pathname.split('/');
      if (parts.length > 2 && parts[1] === 'merchandise') {
        setSelectedProductId(parts[2]);
      } else {
        setSelectedProductId(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToProduct = (productId: string | null) => {
    if (productId) {
      saveViewScroll('merchandise_catalog');
      setSelectedProductId(productId);
      const targetPath = `/merchandise/${productId}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
      }
      resetScrollToTop();
    } else {
      setSelectedProductId(null);
      if (window.location.pathname !== '/merchandise') {
        window.history.pushState(null, '', '/merchandise');
      }
      restoreViewScroll('merchandise_catalog');
    }
  };

  const selectedProduct = useMemo(() => {
    return items.find(item => item.id === selectedProductId);
  }, [items, selectedProductId]);

  const seller = useMemo(() => {
    if (!selectedProduct) return null;
    const sellerId = selectedProduct.userId || 'ASMM-011';
    return members.find(m => m.id.toLowerCase() === sellerId.toLowerCase());
  }, [members, selectedProduct]);

  // Booking Form State for Single Product Detail
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedVariation, setSelectedVariation] = useState<string>('');
  const [deliveryMethod, setDeliveryMethod] = useState<'Ambil di Gelanggang' | 'Penghantaran Pos'>('Ambil di Gelanggang');
  const [selectedPickupGelanggang, setSelectedPickupGelanggang] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState<boolean>(false);

  const isBajuSilat = Boolean(
    selectedProduct &&
    (selectedProduct.id === 'merch-baju-silat' || selectedProduct.name.toLowerCase().includes('baju silat'))
  );

  const isBengkung = Boolean(
    selectedProduct &&
    (selectedProduct.id.includes('bengkung') ||
      selectedProduct.name.toLowerCase().includes('bengkung') ||
      selectedProduct.category.toLowerCase().includes('kelengkapan'))
  );

  // Immediate family unit of current member (children, spouse, dependents)
  const familyMembers = useMemo(() => {
    if (!currentUser) return [];
    return members.filter(m => isFamilyMember(m, currentUser));
  }, [currentUser, members]);

  // Children/dependents of current member
  const myChildren = useMemo(() => {
    if (!currentUser) return [];
    return members.filter(m => isChildOfParent(m, currentUser));
  }, [currentUser, members]);

  // Check if current user is Waris role
  const isWaris = useMemo(() => {
    if (!currentUser) return false;
    return (
      currentUser.role === 'Waris' ||
      currentUser.roles?.includes('Waris') ||
      (currentUser.role && currentUser.role.toLowerCase().includes('waris'))
    );
  }, [currentUser]);

  // Pickup gelanggang options:
  // For Waris: their child's gelanggang (if child got 2 gelanggang, they can choose which one)
  // For normal members: user's own gelanggang name
  const pickupGelanggangOptions = useMemo<{ gelanggang: string; childName?: string }[]>(() => {
    if (!currentUser) return [];

    if (isWaris || myChildren.length > 0) {
      const map = new Map<string, string[]>(); // gelanggang -> child names

      myChildren.forEach(child => {
        if (!child.gelanggang) return;
        const gList = child.gelanggang
          .split(/[,/]+/)
          .map(g => g.trim())
          .filter(Boolean);

        gList.forEach(g => {
          const existing = map.get(g) || [];
          if (!existing.includes(child.name)) {
            existing.push(child.name);
          }
          map.set(g, existing);
        });
      });

      if (map.size > 0) {
        return Array.from(map.entries()).map(([gName, childNames]) => ({
          gelanggang: gName,
          childName: childNames.join(', ')
        }));
      }

      const fallbackG = currentUser.gelanggang && currentUser.gelanggang !== 'Tiada'
        ? currentUser.gelanggang
        : 'Pusaka Warisan';
      return [{
        gelanggang: fallbackG,
        childName: myChildren.map(c => c.name).join(', ') || undefined
      }];
    }

    // Normal member / pesilat:
    const userG = currentUser.gelanggang || 'Pusaka Warisan';
    const gList = userG.split(/[,/]+/).map(g => g.trim()).filter(Boolean);
    if (gList.length > 1) {
      return gList.map(g => ({
        gelanggang: g
      }));
    }

    return [{
      gelanggang: userG
    }];
  }, [currentUser, isWaris, myChildren]);

  // Keep selectedPickupGelanggang valid and synchronized
  useEffect(() => {
    if (pickupGelanggangOptions.length > 0) {
      if (!selectedPickupGelanggang || !pickupGelanggangOptions.some(o => o.gelanggang === selectedPickupGelanggang)) {
        setSelectedPickupGelanggang(pickupGelanggangOptions[0].gelanggang);
      }
    }
  }, [pickupGelanggangOptions, selectedPickupGelanggang]);

  // Maximum bengkung level among member and all their registered family members
  const maxFamilyBengkung = useMemo(() => {
    if (!currentUser) {
      return { maxTier: 0, maxRankIndex: 0, highestRankTitle: 'Tiada', maxColorName: 'Bengkung Hitam' };
    }
    const familyUnit = [currentUser, ...familyMembers];
    let maxTier = 0;
    let maxRankIndex = 0;
    let highestRankTitle = currentUser.bengkung || 'Bengkung Putih';

    familyUnit.forEach(member => {
      const tier = getBengkungColorTier(member.bengkung);
      const rIdx = getRankIndex(member.bengkung);
      if (tier > maxTier) {
        maxTier = tier;
      }
      if (rIdx >= maxRankIndex) {
        maxRankIndex = rIdx;
        const rankInfo = ASMM_BENGKUNG_RANKS[rIdx];
        highestRankTitle = rankInfo ? `${rankInfo.code} (${rankInfo.rankTitle})` : (member.bengkung || highestRankTitle);
      }
    });

    const maxColorName = BENGKUNG_TIER_NAMES[maxTier] || 'Bengkung Putih';
    return { maxTier, maxRankIndex, highestRankTitle, maxColorName };
  }, [currentUser, familyMembers]);

  // Available sizes: for Baju Silat, always match official registration sizing 110cm - 210cm
  const productSizes = useMemo(() => {
    if (!selectedProduct) return [];
    if (isBajuSilat) {
      return ['110cm', '120cm', '130cm', '140cm', '150cm', '160cm', '170cm', '180cm', '190cm', '200cm', '210cm'];
    }
    return selectedProduct.sizes || [];
  }, [selectedProduct, isBajuSilat]);

  // Dynamic unit price based on size (prioritizes sizePrices, then Baju Silat formula, then base price)
  const currentUnitPrice = useMemo(() => {
    return getProductPriceBySize(selectedProduct, selectedSize);
  }, [selectedProduct, selectedSize]);

  // Price range information for the product
  const priceRange = useMemo(() => {
    return selectedProduct ? getProductPriceRange(selectedProduct) : null;
  }, [selectedProduct]);

  // Initialize booking details when product page loads
  useEffect(() => {
    if (selectedProduct) {
      setQuantity(1);
      let defaultSize = '';
      if (isBajuSilat) {
        defaultSize = '150cm';
      } else if (isBengkung && selectedProduct.sizes && selectedProduct.sizes.length > 0) {
        // Select highest allowed bengkung color for user & family
        const allowedOption = selectedProduct.sizes.find(s => {
          return getBengkungColorTier(s) <= maxFamilyBengkung.maxTier;
        });
        defaultSize = allowedOption || selectedProduct.sizes[0];
      } else if (selectedProduct.sizes && selectedProduct.sizes.length > 0) {
        defaultSize = selectedProduct.sizes[0];
      }
      setSelectedSize(defaultSize);

      let defaultVariation = '';
      if (isBengkung && selectedProduct.variations && selectedProduct.variations.length > 0) {
        const allowedVar = selectedProduct.variations.find(v => {
          return !isBengkungVariationLocked(v, defaultSize || 'Hitam', currentUser, maxFamilyBengkung.maxRankIndex).isLocked;
        });
        defaultVariation = allowedVar || selectedProduct.variations[0];
      } else if (selectedProduct.variations && selectedProduct.variations.length > 0) {
        defaultVariation = selectedProduct.variations[0];
      }
      setSelectedVariation(defaultVariation);

      setDeliveryMethod('Ambil di Gelanggang');
      setNotes('');
    }
  }, [selectedProduct, isBajuSilat, isBengkung, maxFamilyBengkung.maxTier, maxFamilyBengkung.maxRankIndex, currentUser]);

  // Keep variation synchronized and valid when bengkung color selection changes
  useEffect(() => {
    if (isBengkung && selectedProduct?.variations && selectedProduct.variations.length > 0 && selectedSize) {
      const lockStatus = isBengkungVariationLocked(
        selectedVariation,
        selectedSize,
        currentUser,
        maxFamilyBengkung.maxRankIndex
      );
      if (!selectedVariation || lockStatus.isLocked) {
        const allowedVar = selectedProduct.variations.find(v => {
          return !isBengkungVariationLocked(v, selectedSize, currentUser, maxFamilyBengkung.maxRankIndex).isLocked;
        });
        if (allowedVar) {
          setSelectedVariation(allowedVar);
        }
      }
    }
  }, [selectedSize, isBengkung, maxFamilyBengkung.maxRankIndex, currentUser, selectedProduct]);

  // Filter catalog items
  const filteredCatalog = useMemo(() => {
    return items.filter(item => {
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [items, categoryFilter, searchTerm]);

  // Place order flow (Shopee flow)
  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !selectedProduct) {
      showToast('error', 'Log Masuk Diperlukan', 'Sila log masuk ke portal persatuan terlebih dahulu untuk menempah barangan.');
      return;
    }

    if (isBengkung) {
      if (selectedSize) {
        const optionTier = getBengkungColorTier(selectedSize);
        if (optionTier > maxFamilyBengkung.maxTier) {
          showToast(
            'error',
            'Peringkat Bengkung Belum Layak',
            `Anda dan ahli keluarga anda belum mencapai tahap Bengkung ${selectedSize}. Pembelian bengkung dihadkan mengikut kelayakan tertinggi (sehingga ${maxFamilyBengkung.maxColorName}).`
          );
          return;
        }
      }

      if (selectedVariation) {
        const culaLock = isBengkungVariationLocked(
          selectedVariation,
          selectedSize || 'Hitam',
          currentUser,
          maxFamilyBengkung.maxRankIndex
        );
        if (culaLock.isLocked) {
          showToast(
            'error',
            'Peringkat Cula Belum Layak',
            culaLock.reason || `Peringkat ${selectedVariation} belum dicapai oleh anda atau ahli keluarga berdaftar.`
          );
          return;
        }
      }
    }

    setIsSubmitting(true);
    const orderId = `order-${Date.now()}`;
    const invoiceId = `inv-merch-${Date.now().toString().slice(-4)}`;
    const shippingFee = deliveryMethod === 'Penghantaran Pos' ? 8.0 : 0.0;
    const unitPrice = currentUnitPrice;
    const totalAmount = unitPrice * quantity + shippingFee;

    const fullItemName = selectedProduct.name + (selectedVariation ? ` (${selectedVariation})` : '');
    const effectivePickupGelanggang = selectedPickupGelanggang || pickupGelanggangOptions[0]?.gelanggang || currentUser.gelanggang || 'Pusaka Warisan';

    const newOrder: MerchandiseOrder = {
      id: orderId,
      memberId: currentUser.id,
      memberName: currentUser.name,
      phone: currentUser.phone,
      gelanggang: effectivePickupGelanggang,
      pickupLocation: effectivePickupGelanggang,
      itemId: selectedProduct.id,
      itemName: fullItemName,
      itemCategory: selectedProduct.category,
      quantity,
      size: selectedSize || undefined,
      totalAmount,
      deliveryMethod,
      notes: notes || undefined,
      status: 'Menunggu Kelulusan',
      invoiceId,
      createdAt: new Date().toISOString().split('T')[0],
      paymentMethod: 'DuitNow QR',
      paymentStatus: 'Belum Dibayar',
      sellerId: selectedProduct.userId || 'ASMM-001'
    };

    // Auto-generate unpaid invoice so user can pay via Bayaran tab
    const newInvoice: InvoiceItem = {
      id: invoiceId,
      invoiceNo: `INV-${Date.now().toString().slice(-6)}`,
      memberId: currentUser.id,
      memberName: currentUser.name,
      memberIcNo: currentUser.icNo,
      memberPhone: currentUser.phone,
      gelanggang: effectivePickupGelanggang,
      category: 'Lain-lain',
      title: `Merchandise: ${selectedProduct.name} (x${quantity})`,
      description: `Tempahan barangan merchandise ASMM. Kuantiti: ${quantity}${selectedSize ? `, ${isBengkung ? 'Warna' : 'Saiz'}: ${selectedSize} (RM${unitPrice.toFixed(2)})` : ''}${selectedVariation ? `, Variasi: ${selectedVariation}` : ''}. Kaedah: ${deliveryMethod} (Pusat Ambilan: ${effectivePickupGelanggang}).`,
      amount: totalAmount,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      createdAt: new Date().toISOString().split('T')[0],
      status: 'Belum Dibayar'
    };

    try {
      // Store in memory draft ONLY.
      // Do NOT record to database until user uploads receipt and clicks submit!
      setPlacedOrderForQr(newOrder);
      setPendingInvoiceForQr(newInvoice);

      showToast(
        'info',
        'Lengkapkan Pembayaran',
        `Sila imbas kod QR DuitNow dan muat naik resit bayaran untuk melengkapkan tempahan.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSubmitted = async (updatedOrder: MerchandiseOrder) => {
    try {
      // 1. Record invoice to database
      if (pendingInvoiceForQr) {
        await saveInvoiceDB(pendingInvoiceForQr);
      }
      // 2. Record order to database ONLY AFTER resit is uploaded and submitted!
      await saveOrder(updatedOrder);

      showToast(
        'success',
        'Tempahan & Resit Berjaya Dihantar!',
        `Tempahan #${updatedOrder.id.slice(-6).toUpperCase()} dan resit pembayaran telah dihantar kepada penjual untuk pengesahan.`
      );

      setPlacedOrderForQr(null);
      setPendingInvoiceForQr(null);
      navigateToProduct(null);

      if (onNavigateTab) {
        onNavigateTab('pembelian');
      } else {
        window.history.pushState(null, '', '/pembelian');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (err) {
      showToast('error', 'Ralat', 'Gagal merekodkan tempahan. Sila cuba lagi.');
    }
  };

  const handleQrModalClose = () => {
    // User closed or cancelled payment modal without submitting receipt:
    // DO NOT record order or invoice in DB. Keep user on product view.
    setPlacedOrderForQr(null);
    setPendingInvoiceForQr(null);
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText="ASMM Merchs"
        badgeIcon={ShoppingBag}
        title="Kedai"
      >
        {isManagement && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setEditingProduct(null);
                setIsProductEditOpen(true);
              }}
              className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-2xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Tambah Produk</span>
            </button>
            <button
              onClick={() => {
                if (onNavigateTab) {
                  onNavigateTab('seller');
                } else {
                  window.history.pushState(null, '', '/seller/orders');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }
              }}
              className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-700 font-extrabold text-xs rounded-2xl border border-slate-200 transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <Package className="w-4 h-4 text-slate-500" />
              <span>Portal Penjual</span>
            </button>
          </div>
        )}
      </PageHeader>

      {/* RENDER TAB 1: PRODUCT SINGLE DETAIL VIEW */}
      {selectedProduct ? (
        <div className="space-y-6">
          {/* Breadcrumb / Back button */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigateToProduct(null)}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer text-slate-600 hover:text-slate-900 shrink-0"
              title="Kembali ke Katalog"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setCategoryFilter('all');
                navigateToProduct(null);
              }}
              className="text-xs text-slate-500 hover:text-asmm-red font-bold transition-colors cursor-pointer hover:underline"
              title="Kembali ke semua barangan katalog"
            >
              Katalog
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <button
              type="button"
              onClick={() => {
                setCategoryFilter(selectedProduct.category);
                navigateToProduct(null);
              }}
              className="text-xs text-slate-500 hover:text-asmm-red font-bold transition-colors cursor-pointer hover:underline"
              title={`Tapis barangan mengikut kategori ${selectedProduct.category}`}
            >
              {selectedProduct.category}
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs text-slate-800 font-extrabold truncate max-w-[200px] sm:max-w-xs" aria-current="page">
              {selectedProduct.name}
            </span>
          </nav>

          {/* Product Detail Layout (Shopee flow structure) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 shadow-xs">
            {/* Image Box */}
            <div className="lg:col-span-5 space-y-4">
              <div className="aspect-square bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-inner">
                <img
                  src={selectedProduct.photoUrl}
                  alt={selectedProduct.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product Configuration & Buy Box */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                <div>
                  <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full text-asmm-red bg-red-50 border border-red-100 uppercase tracking-wider">
                    {selectedProduct.category}
                  </span>
                  <h2 className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-2">
                    {selectedProduct.name}
                  </h2>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <div className="text-2xl font-extrabold text-asmm-red">
                      RM {currentUnitPrice.toFixed(2)}
                    </div>
                    <span className="text-slate-300">|</span>
                    <div className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${selectedProduct.stockStatus === 'Dalam Stok' ? 'bg-emerald-500' : selectedProduct.stockStatus === 'Stok Rendah' ? 'bg-amber-500' : 'bg-rose-500'
                        }`} />
                      <span className="text-xs font-bold text-slate-500">{selectedProduct.stockStatus}</span>
                    </div>
                  </div>
                </div>

                {isManagement && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingProduct(selectedProduct);
                      setIsProductEditOpen(true);
                    }}
                    className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs rounded-xl transition-all shadow-xs flex items-center gap-2 cursor-pointer shrink-0"
                    title="Kemaskini Butiran & Harga Mengikut Saiz"
                  >
                    <Edit3 className="w-4 h-4 text-amber-400" />
                    <span>Kemaskini Butiran</span>
                  </button>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Deskripsi Produk</h4>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {selectedProduct.description}
                </p>
              </div>

              {/* Specifications / Detail Jualan (Shopee specs style) */}
              {(Boolean(selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0) || isBajuSilat) && (
                <div className="space-y-2">
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Spesifikasi Produk</h4>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs space-y-3">
                    {selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(selectedProduct.specifications).map(([key, val]) => (
                          <div key={key} className="flex gap-2">
                            <span className="font-bold text-slate-400 w-24 shrink-0">{key}</span>
                            <span className="font-bold text-slate-700">{val}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Size Chart Row - Grouped at the most bottom of Spesifikasi Produk */}
                    {isBajuSilat && (
                      <div className="pt-2.5 border-t border-slate-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-400 w-24 shrink-0">Carta Saiz</span>
                          <span className="font-bold text-slate-700">
                            {productSizes.length > 0 ? `${productSizes[0]} – ${productSizes[productSizes.length - 1]}` : '110cm – 210cm'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsSizeChartOpen(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-asmm-red hover:text-red-700 transition-colors cursor-pointer bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl border border-red-200 shadow-xs shrink-0"
                          title="Buka Carta Cadangan Saiz Uniform"
                        >
                          <Ruler className="w-3.5 h-3.5" />
                          <span>Buka Carta Cadangan Saiz</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seller Shop Profile Section (Shopee layout flow) */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-asmm-red text-white flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 uppercase">
                    {seller?.name ? formatSellerName(seller.name).slice(0, 2) : 'AS'}
                  </div>
                  <div>
                    <h4 className="font-extrabold text-slate-800 text-[11px] sm:text-xs leading-none">{formatSellerName(seller?.name)}</h4>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-slate-500 font-bold">
                      <span className="text-asmm-red">{seller?.role || 'Pengurus Kedah'}</span>
                      <span className="text-slate-350">•</span>
                      <span>📍 {seller?.gelanggang === 'Semua Gelanggang' ? 'ASMM' : (seller?.gelanggang || 'ASMM')}</span>
                    </div>
                  </div>
                </div>
                {seller?.phone && (() => {
                  let cleanPhone = seller.phone.replace(/[^0-9]/g, '');
                  if (cleanPhone.startsWith('0')) {
                    cleanPhone = '6' + cleanPhone;
                  } else if (!cleanPhone.startsWith('60') && cleanPhone.startsWith('1')) {
                    cleanPhone = '60' + cleanPhone;
                  }
                  return (
                    <a
                      href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                        `Salam sejahtera, saya berminat untuk bertanya mengenai produk *${selectedProduct.name}* di portal ASMM:\n${window.location.origin}/merchandise/${selectedProduct.id}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer no-underline shrink-0"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Hubungi Whatsapp
                    </a>
                  );
                })()}
              </div>

              {/* Order Form */}
              <form onSubmit={handlePlaceOrder} className="space-y-4 pt-4 border-t border-slate-100 text-xs">
                {/* Product Options: Bengkung has Warna above Variasi, Baju Silat & other items have Variasi above Pilihan Saiz */}
                {(() => {
                  const sizeSelectionBlock = productSizes && productSizes.length > 0 && (
                    <div key="sizes-block">
                      <label className="block text-slate-700 font-bold mb-1.5">
                        {isBengkung
                          ? 'Pilihan Warna Bengkung *'
                          : `Pilihan Saiz ${isBajuSilat ? '(Mengikut Ketinggian)' : ''} *`}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {productSizes.map(s => {
                          const isSelected = selectedSize === s;
                          const optionTier = isBengkung ? getBengkungColorTier(s) : 0;
                          const isLocked = isBengkung && (!currentUser || optionTier > maxFamilyBengkung.maxTier);

                          if (isLocked) {
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => {
                                  showToast(
                                    'error',
                                    'Peringkat Belum Layak',
                                    `Peringkat Bengkung ${s} memerlukan kenaikan taraf bengkung. Tahap tertinggi anda atau ahli keluarga berdaftar: ${maxFamilyBengkung.maxColorName}.`
                                  );
                                }}
                                className="px-3.5 py-1.5 rounded-xl border border-dashed border-slate-300 bg-slate-100 text-slate-400 text-xs font-bold transition-all cursor-not-allowed flex items-center gap-1.5 opacity-60 hover:opacity-80"
                                title={`Terkunci: Memerlukan kelayakan Bengkung ${s}`}
                              >
                                <Lock className="w-3 h-3 text-slate-400" />
                                <span className="line-through">{s}</span>
                                <span className="text-[10px] font-normal text-slate-400 no-underline">(Terkunci)</span>
                              </button>
                            );
                          }

                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setSelectedSize(s)}
                              className={`px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${isSelected
                                ? 'bg-slate-900 text-white border-slate-900 shadow-xs ring-2 ring-slate-300'
                                : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>

                      {/* Bengkung Eligibility Trust Banner */}
                      {isBengkung && currentUser && (
                        <div className="mt-2 text-xs flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200/80 rounded-xl text-slate-600">
                          <Shield className="w-4 h-4 text-asmm-red shrink-0" />
                          <span>
                            Kelayakan tertinggi anda & keluarga: <strong className="text-slate-800">{maxFamilyBengkung.highestRankTitle}</strong> (Dihadkan mengikut tahap warna & cula yang dicapai).
                          </span>
                        </div>
                      )}

                      {isBengkung && !currentUser && (
                        <div className="mt-2 text-xs flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                          <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>
                            Sila log masuk untuk membeli bengkung mengikut peringkat kelayakan anda atau keluarga berdaftar.
                          </span>
                        </div>
                      )}
                    </div>
                  );

                  const variationsSelectionBlock = selectedProduct.variations && selectedProduct.variations.length > 0 && (
                    <div key="variations-block">
                      <label className="block text-slate-700 font-bold mb-1.5">
                        {isBengkung ? 'Pilihan Cula / Variasi *' : 'Pilihan Variasi *'}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.variations.map(v => {
                          const isSelected = selectedVariation === v;
                          const culaLock = isBengkung
                            ? isBengkungVariationLocked(v, selectedSize || 'Hitam', currentUser, maxFamilyBengkung.maxRankIndex)
                            : { isLocked: false };

                          if (culaLock.isLocked) {
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => {
                                  showToast(
                                    'error',
                                    'Peringkat Cula Belum Layak',
                                    culaLock.reason || `Peringkat ${v} memerlukan kenaikan taraf cula. Tahap tertinggi anda atau ahli keluarga berdaftar: ${maxFamilyBengkung.highestRankTitle}.`
                                  );
                                }}
                                className="px-3 py-1.5 rounded-xl border border-dashed border-slate-300 bg-slate-100 text-slate-400 text-xs font-bold transition-all cursor-not-allowed flex items-center gap-1.5 opacity-60 hover:opacity-80"
                                title={`Terkunci: ${culaLock.reason}`}
                              >
                                <Lock className="w-3 h-3 text-slate-400" />
                                <span className="line-through">{v}</span>
                                <span className="text-[10px] font-normal text-slate-400 no-underline">(Terkunci)</span>
                              </button>
                            );
                          }

                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSelectedVariation(v)}
                              className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${isSelected
                                ? 'bg-slate-900 text-white border-slate-900 shadow-xs ring-2 ring-slate-300'
                                : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );

                  return isBengkung ? (
                    <>
                      {sizeSelectionBlock}
                      {variationsSelectionBlock}
                    </>
                  ) : (
                    <>
                      {variationsSelectionBlock}
                      {sizeSelectionBlock}
                    </>
                  );
                })()}

                {/* Quantity & Delivery Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Quantity */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Kuantiti Tempahan *</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-250 flex items-center justify-center font-extrabold text-slate-700 cursor-pointer"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                        className="w-12 h-8 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(q => q + 1)}
                        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-250 flex items-center justify-center font-extrabold text-slate-700 cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Delivery Mode */}
                  <div>
                    <label className="block text-slate-700 font-bold text-xs mb-1">Kaedah Penghantaran *</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDeliveryMethod('Ambil di Gelanggang')}
                        className={`px-3 py-1.5 rounded-lg border font-bold text-xs transition-all cursor-pointer flex items-center gap-1.5 ${deliveryMethod === 'Ambil di Gelanggang'
                          ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                          }`}
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        <span>Pickup</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Pickup Location Point Selection */}
                {deliveryMethod === 'Ambil di Gelanggang' && (
                  <div className="space-y-2">
                    {currentUser ? (
                      pickupGelanggangOptions.length > 1 ? (
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-asmm-red" />
                              <span>Pusat Pengambilan</span>
                            </label>
                          </div>
                          <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                            Sila pilih gelanggang pengambilan untuk serahan pesanan ini:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                            {pickupGelanggangOptions.map(option => {
                              const isSelected = selectedPickupGelanggang === option.gelanggang;
                              return (
                                <button
                                  key={option.gelanggang}
                                  type="button"
                                  onClick={() => setSelectedPickupGelanggang(option.gelanggang)}
                                  className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex items-start gap-2.5 ${isSelected
                                      ? 'border-asmm-red bg-white ring-2 ring-red-100 shadow-xs'
                                      : 'border-slate-200 bg-white hover:border-slate-300'
                                    }`}
                                >
                                  <div className={`w-4 h-4 rounded-full border mt-0.5 flex items-center justify-center shrink-0 ${isSelected ? 'border-asmm-red bg-asmm-red text-white' : 'border-slate-300'
                                    }`}>
                                    {isSelected && <Check className="w-2.5 h-2.5" />}
                                  </div>
                                  <div className="min-w-0">
                                    <div className={`text-xs font-black ${isSelected ? 'text-asmm-red' : 'text-slate-900'}`}>
                                      {option.gelanggang}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-medium line-clamp-2 mt-0.5">
                                      {getGelanggangAddress(option.gelanggang)}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-9 h-9 rounded-xl bg-red-50 text-asmm-red border border-red-100 flex items-center justify-center shrink-0">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                Pusat Pengambilan
                              </span>
                              <div className="text-xs font-black text-slate-900 truncate">
                                {pickupGelanggangOptions[0]?.gelanggang || currentUser.gelanggang || 'Pusaka Warisan'}
                              </div>
                              <div className="text-[10px] text-slate-500 font-medium mt-0.5">
                                {getGelanggangAddress(pickupGelanggangOptions[0]?.gelanggang || currentUser.gelanggang)}
                              </div>
                            </div>
                          </div>
                          <span className="text-[10px] font-black px-2.5 py-1 rounded-full bg-slate-200/70 text-slate-700 shrink-0">
                            Ditetapkan
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-800 flex items-center gap-2">
                        <Info className="w-4 h-4 shrink-0 text-amber-600" />
                        <span>Sila log masuk untuk mengesahkan gelanggang serahan pesanan anda.</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Nota Tambahan</label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Contoh: Saiz khas, arahan pembungkusan, warna lain."
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-asmm-red"
                  />
                </div>

                {/* Submit Action */}
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={isSubmitting || selectedProduct.stockStatus === 'Tiada Stok'}
                    className="flex-1 py-3 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>{isSubmitting ? 'Memproses...' : 'Sahkan & Bayar Kod QR'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : (
        /* CATALOG GRID (NORMAL VIEW) */
        <div className="space-y-6">
          {/* Controls Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari barangan kedai..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900"
              />
            </div>

            {/* Category Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap w-full sm:w-auto pb-0.5">
              <span className="text-xs text-slate-400 font-bold mr-1 whitespace-nowrap shrink-0">Kategori:</span>
              {[
                { id: 'all', label: 'Semua' },
                { id: 'Pakaian', label: 'Pakaian' },
                { id: 'Kelengkapan', label: 'Kelengkapan Silat' },
                { id: 'Aksesori', label: 'Aksesori' }
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 border transition-all cursor-pointer ${categoryFilter === cat.id
                    ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                    }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid list of catalog */}
          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
              <div className="w-8 h-8 border-3 border-asmm-red border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-400 font-medium mt-3">Memuatkan katalog barangan...</p>
            </div>
          ) : filteredCatalog.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCatalog.map(item => (
                <div
                  key={item.id}
                  onClick={() => navigateToProduct(item.id)}
                  className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between group cursor-pointer"
                >
                  <div>
                    {/* Item Image */}
                    <div className="relative aspect-square bg-slate-100 overflow-hidden border-b border-slate-200">
                      <img
                        src={item.photoUrl}
                        alt={item.name}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-slate-900/90 text-white text-[9px] font-extrabold rounded-full tracking-wider uppercase">
                        {item.category}
                      </span>
                      {isManagement && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProduct(item);
                            setIsProductEditOpen(true);
                          }}
                          className="absolute top-3 right-3 p-2 bg-white/95 hover:bg-white text-slate-700 hover:text-asmm-red rounded-full shadow-md transition-all cursor-pointer border border-slate-200 hover:scale-105"
                          title={`Kemaskini Butiran & Harga ${item.name}`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Details */}
                    <div className="p-5 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-extrabold text-sm sm:text-base text-slate-900 leading-tight">
                          {item.name}
                        </h4>
                        <span className="text-sm font-extrabold text-asmm-red whitespace-nowrap shrink-0">
                          {getProductPriceRange(item).label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed line-clamp-3">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Footer Action */}
                  <div className="px-5 pb-5 pt-1 flex items-center justify-between gap-3 border-t border-slate-55 mt-auto">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${item.stockStatus === 'Dalam Stok' ? 'bg-emerald-500' : item.stockStatus === 'Stok Rendah' ? 'bg-amber-500' : 'bg-rose-500'
                        }`} />
                      <span className="text-[10px] font-bold text-slate-500">{item.stockStatus}</span>
                    </div>

                    <span className="text-xs text-slate-400 group-hover:text-asmm-red font-extrabold flex items-center gap-1 transition-all">
                      <span>Lihat Butiran</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center space-y-2">
              <Package className="w-12 h-12 text-slate-300 mx-auto" />
              <h4 className="font-extrabold text-sm text-slate-700">Tiada Barangan Dijumpai</h4>
              <p className="text-xs text-slate-400 font-medium">Cuba ubah kata kunci carian atau penapis kategori anda.</p>
            </div>
          )}
        </div>
      )}

      {/* Instant DuitNow QR Payment Modal */}
      <DuitNowQrModal
        isOpen={Boolean(placedOrderForQr)}
        onClose={handleQrModalClose}
        order={placedOrderForQr}
        seller={seller}
        isNewOrder={true}
        onPaymentSubmitted={handlePaymentSubmitted}
      />

      {/* Uniform Size Chart Modal (Same as Registration) */}
      {isSizeChartOpen && (
        <UniformSizeChartModal
          isOpen={isSizeChartOpen}
          onClose={() => setIsSizeChartOpen(false)}
          selectedSize={selectedSize}
          availableSizes={productSizes}
          sizePrices={selectedProduct?.sizePrices}
          basePrice={selectedProduct?.price}
          productTitle={selectedProduct?.name}
          onSelectSize={(size) => {
            setSelectedSize(size);
            setIsSizeChartOpen(false);
          }}
        />
      )}

      {/* Product Edit / Add Modal */}
      {isProductEditOpen && (
        <ProductEditModal
          isOpen={isProductEditOpen}
          onClose={() => {
            setIsProductEditOpen(false);
            setEditingProduct(null);
          }}
          product={editingProduct}
          onSave={async (item) => {
            await saveItem(item);
          }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};
